import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, IDropdownOption, Icon, Stack, Text } from '@fluentui/react';
import {
    getAutoRefreshIntervalFromLocalStorage,
    setAutoRefreshIntervalInLocalStorage,
} from '../../../Common/util/localStorageUtils';

// Auto-refresh interval choices (in minutes). 0 disables the feature.
const autoRefreshOptions: IDropdownOption[] = [
    { key: 0, text: 'off' },
    { key: 1, text: '1m' },
    { key: 5, text: '5m' },
    { key: 10, text: '10m' },
];

const formatCountdown = (secondsRemaining: number): string => {
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const renderTitle = (options?: IDropdownOption[]): JSX.Element => {
    const option = options && options[0];
    const minutes = option ? (option.key as number) : 0;
    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
            <Icon iconName="Refresh" />
            <Text>
                {minutes > 0
                    ? `Auto refresh every ${option!.text}`
                    : 'Auto refresh off'}
            </Text>
        </Stack>
    );
};

/**
 * Auto-refresh control for the Release Details and Release List pages,
 * mirroring the "Auto refresh every X minutes" dropdown in the IcM portal.
 * Shows a live countdown to the next refresh, and by default refreshes the page
 * data in place (via the onRefresh callback) so the view does not flash to a
 * spinner on each refresh. The selected interval is persisted to localStorage
 * and shared by every page that hosts this control.
 */
interface IAutoRefreshProps {
    // Called each time the interval elapses. When provided, the page is
    // refreshed in place (no full reload, so no page "flash"). If omitted, the
    // control falls back to a full window reload.
    onRefresh?: () => void;
}

export const AutoRefresh: React.FC<IAutoRefreshProps> = ({ onRefresh }) => {
    const [intervalMinutes, setIntervalMinutes] = useState<number>(
        getAutoRefreshIntervalFromLocalStorage
    );
    const [secondsRemaining, setSecondsRemaining] = useState<number>(
        () => getAutoRefreshIntervalFromLocalStorage() * 60
    );

    // Keep the latest onRefresh callback available to the interval without
    // restarting the timer whenever the callback identity changes.
    const onRefreshRef = useRef(onRefresh);
    useEffect(() => {
        onRefreshRef.current = onRefresh;
    }, [onRefresh]);

    // Restart the countdown whenever the selected interval changes.
    useEffect(() => {
        setSecondsRemaining(intervalMinutes * 60);
    }, [intervalMinutes]);

    // Tick the countdown down once per second while auto-refresh is enabled.
    useEffect(() => {
        if (intervalMinutes <= 0) {
            return;
        }
        const timerId = window.setInterval(() => {
            setSecondsRemaining((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(timerId);
    }, [intervalMinutes]);

    // Fire the refresh once the countdown reaches zero, then restart it.
    useEffect(() => {
        if (intervalMinutes > 0 && secondsRemaining === 0) {
            if (onRefreshRef.current) {
                onRefreshRef.current();
            } else {
                window.location.reload();
            }
            setSecondsRemaining(intervalMinutes * 60);
        }
    }, [intervalMinutes, secondsRemaining]);

    const onChange = useCallback(
        (_?: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
            if (!option) {
                return;
            }
            const minutes = option.key as number;
            setIntervalMinutes(minutes);
            setAutoRefreshIntervalInLocalStorage(minutes);
        },
        []
    );

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Dropdown
                ariaLabel="Auto refresh interval"
                title="Automatically refresh this page on the selected interval"
                selectedKey={intervalMinutes}
                options={autoRefreshOptions}
                onChange={onChange}
                onRenderTitle={renderTitle}
                styles={{
                    root: { minWidth: 190 },
                    dropdown: { minWidth: 190 },
                }}
            />
            {intervalMinutes > 0 && (
                <Text
                    variant="small"
                    aria-live="polite"
                    styles={{ root: { whiteSpace: 'nowrap', minWidth: 70 } }}
                >
                    next in {formatCountdown(secondsRemaining)}
                </Text>
            )}
        </Stack>
    );
};
